import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  IconButton,
  Tooltip,
  Paper
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import BuildIcon from '@material-ui/icons/Build';
import KeyIcon from '@material-ui/icons/VpnKey';
import InfoIcon from '@material-ui/icons/Info';
import PersonIcon from '@material-ui/icons/Person';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import OpenPGPKeySetupWizard from './OpenPGPKeySetupWizard';
import OpenPGPKeyImportDialog from './OpenPGPKeyImportDialog';
import { transceive } from './actions';
import { hexStringToString } from './util';
import { detectOpenPGPCapabilities } from './capabilityDetection';

// OpenPGP key types
const OPENPGP_KEY_TYPES = [
  { id: 'sig', tag: 'C7', name: 'Signature Key', description: 'Sign emails and documents' },
  { id: 'dec', tag: 'C8', name: 'Decryption Key', description: 'Decrypt encrypted messages' },
  { id: 'aut', tag: 'C9', name: 'Authentication Key', description: 'SSH and authentication' }
];

const OpenPGP = ({ dispatch }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [keyStatuses, setKeyStatuses] = useState({});
  const [personalInfo, setPersonalInfo] = useState({});
  const [cardInfo, setCardInfo] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedKeyType, setSelectedKeyType] = useState(null);
  const [selectedKeyName, setSelectedKeyName] = useState('');

  useEffect(() => {
    loadKeyStatuses();
    loadPersonalInfo();
    loadCardInfo();
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      const caps = await detectOpenPGPCapabilities(dispatch);
      setCapabilities(caps);
    } catch (error) {
      console.error('Failed to load capabilities:', error);
    }
  };

  const loadCardInfo = async () => {
    try {
      await dispatch(transceive('00A4040006D27600012401'));

      // Get application related data (tag 0x6E)
      const response = await dispatch(transceive('00CA006E00'));
      if (response.endsWith('9000')) {
        setCardInfo({ status: 'Active' });
      }
    } catch (error) {
      console.error('Failed to load card info:', error);
    }
  };

  const loadPersonalInfo = async () => {
    try {
      await dispatch(transceive('00A4040006D27600012401'));

      const info = {};
      const tags = [
        { tag: '005E', name: 'login' },
        { tag: '5F50', name: 'url' },
        { tag: '005B', name: 'name' },
        { tag: '5F2D', name: 'language' },
        { tag: '5F35', name: 'sex' }
      ];

      for (const t of tags) {
        try {
          const apdu = `00CA${t.tag}00`;
          const response = await dispatch(transceive(apdu));

          if (response.slice(-4) === '9000' && response.length > 4) {
            const value = hexStringToString(response.slice(0, -4));
            if (value && value.length > 0) {
              info[t.name] = value;
            }
          }
        } catch (error) {
          console.error(`Failed to read ${t.name}:`, error);
        }
      }

      setPersonalInfo(info);
    } catch (error) {
      console.error('Failed to load personal info:', error);
    }
  };

  const loadKeyStatuses = async () => {
    setLoading(true);
    const statuses = {};

    try {
      await dispatch(transceive('00A4040006D27600012401'));

      // Read key fingerprints (TAG 0xC5)
      const fingerprintAPDU = '00CA00C500';
      const fpResponse = await dispatch(transceive(fingerprintAPDU));

      if (fpResponse.slice(-4) === '9000') {
        const data = fpResponse.slice(0, -4);
        // Parse fingerprints (3 keys: signature, decryption, authentication)
        // Each fingerprint is 20 bytes (40 hex chars)
        const keyTypes = ['sig', 'dec', 'aut'];
        for (let i = 0; i < 3 && i * 40 < data.length; i++) {
          const fingerprint = data.substring(i * 40, (i + 1) * 40);
          statuses[keyTypes[i]] = {
            hasKey: fingerprint && !/^0+$/.test(fingerprint),
            fingerprint: fingerprint
          };
        }
      }

      setKeyStatuses(statuses);
    } catch (error) {
      console.error('Failed to load key statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const getKeyStatusChip = (status) => {
    if (!status) {
      return <Chip label="Unknown" size="small" />;
    }

    if (status.hasKey) {
      return <Chip label="Key Present" size="small" color="success" icon={<KeyIcon />} />;
    }

    return <Chip label="No Key" size="small" color="default" />;
  };

  const formatFingerprint = (fp) => {
    if (!fp || fp.length !== 40) return 'N/A';
    // Format as: XXXX XXXX XXXX XXXX XXXX  XXXX XXXX XXXX XXXX XXXX
    return fp.match(/.{4}/g).join(' ').replace(/^(.{24})/, '$1  ');
  };

  const handleImportKey = async (keyTypeId) => {
    const keyType = OPENPGP_KEY_TYPES.find(k => k.id === keyTypeId);
    setSelectedKeyType(keyTypeId);
    setSelectedKeyName(keyType?.name || '');
    setImportDialogOpen(true);
  };

  const handleImportSuccess = (keyType) => {
    loadKeyStatuses();
    loadPersonalInfo();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" component="h1">
            OpenPGP Management
          </Typography>
          <Box>
            <Tooltip title="Refresh status">
              <IconButton onClick={() => { loadKeyStatuses(); loadPersonalInfo(); }} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>What is OpenPGP?</AlertTitle>
          OpenPGP is an open standard for email encryption and digital signatures. Your CanoKey can store
          OpenPGP keys for secure communication using tools like GnuPG, Thunderbird, and others.
        </Alert>

        {cardInfo && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Card Status</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Applet Status</strong></TableCell>
                    <TableCell><Chip label={cardInfo.status} size="small" color="success" /></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {Object.keys(personalInfo).length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6">Personal Information</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  {personalInfo.name && (
                    <TableRow>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell>{personalInfo.name}</TableCell>
                    </TableRow>
                  )}
                  {personalInfo.login && (
                    <TableRow>
                      <TableCell><strong>Login</strong></TableCell>
                      <TableCell>{personalInfo.login}</TableCell>
                    </TableRow>
                  )}
                  {personalInfo.url && (
                    <TableRow>
                      <TableCell><strong>Public Key URL</strong></TableCell>
                      <TableCell>{personalInfo.url}</TableCell>
                    </TableRow>
                  )}
                  {personalInfo.language && (
                    <TableRow>
                      <TableCell><strong>Language</strong></TableCell>
                      <TableCell>{personalInfo.language}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </Box>

      <Box sx={{ mb: 4 }}>
        <Card variant="outlined" sx={{ bgcolor: '#f5f5f5', border: '2px solid #1976d2' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <BuildIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h6">OpenPGP Key Setup Wizard</Typography>
                <Typography variant="body2" color="textSecondary">
                  Configure all your OpenPGP keys in one simple wizard
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Recommended for First-Time Setup</AlertTitle>
              The wizard will guide you through:
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Choosing between card-generated or external keys</li>
                <li>Selecting appropriate key algorithms (RSA, Ed25519, etc.)</li>
                <li>Understanding backup strategies</li>
                <li>Generating or importing keys for all key types</li>
              </ul>
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<BuildIcon />}
              onClick={() => setWizardOpen(true)}
              fullWidth
            >
              Launch OpenPGP Key Setup Wizard
            </Button>
          </CardContent>
        </Card>
      </Box>

      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Key Slots
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {OPENPGP_KEY_TYPES.map((keyType) => {
          const status = keyStatuses[keyType.id];
          return (
            <Grid item xs={12} key={keyType.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                      {keyType.name}
                    </Typography>
                    {getKeyStatusChip(status)}
                  </Box>

                  <Typography variant="body2" color="textSecondary" paragraph>
                    {keyType.description}
                  </Typography>

                  {status && (
                    <Box sx={{ mt: 2 }}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                          Fingerprint:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {status.hasKey ? formatFingerprint(status.fingerprint) : 'No key present'}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => handleImportKey(keyType.id)}
                    disabled={loading}
                  >
                    Import Key
                  </Button>
                  <Button
                    size="small"
                    startIcon={<InfoIcon />}
                    disabled={!status?.hasKey}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Alert severity="warning">
          <AlertTitle>Important: Key Backup Strategy</AlertTitle>
          <Typography variant="body2" paragraph>
            <strong>Card-Generated Keys:</strong> Keys generated inside the CanoKey device cannot be exported or backed up.
            If you lose your device, these keys cannot be recovered. You'll need to generate new keys and notify your contacts.
          </Typography>
          <Typography variant="body2">
            <strong>External Keys:</strong> If you need backup capability, generate keys externally using GnuPG,
            keep encrypted backups, and import them to your CanoKey device using the "keytocard" command.
            Use the wizard for guided setup.
          </Typography>
        </Alert>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Alert severity="info">
          <AlertTitle>Using Your OpenPGP Card</AlertTitle>
          <Typography variant="body2">
            • <strong>Check card status:</strong> Run <code>gpg --card-status</code> in terminal<br />
            • <strong>Generate keys on card:</strong> Use <code>gpg --card-edit</code> then <code>generate</code><br />
            • <strong>Import external keys:</strong> Use <code>gpg --edit-key YOUR_KEY_ID</code> then <code>keytocard</code><br />
            • <strong>Sign/Encrypt:</strong> GnuPG will automatically use your card when the key is needed<br />
            • <strong>Setup Wizard:</strong> Use the wizard above for guided configuration
          </Typography>
        </Alert>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Alert severity="success">
          <AlertTitle>Integration with Email Clients</AlertTitle>
          <Typography variant="body2">
            After setting up your keys, you can use your CanoKey with:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li><strong>Thunderbird:</strong> Install Enigmail or use built-in OpenPGP support</li>
              <li><strong>Outlook:</strong> Use Gpg4win with GpgOL plugin</li>
              <li><strong>Apple Mail:</strong> Use GPGTools</li>
              <li><strong>Command line:</strong> Use GnuPG directly for signing/encryption</li>
            </ul>
          </Typography>
        </Alert>
      </Box>

      {/* Wizard Dialog */}
      <OpenPGPKeySetupWizard
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
          loadKeyStatuses(); // Refresh after wizard completes
          loadPersonalInfo();
        }}
        dispatch={dispatch}
      />

      {/* Key Import Dialog */}
      <OpenPGPKeyImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        keyType={selectedKeyType}
        keyTypeName={selectedKeyName}
        dispatch={dispatch}
        onImportSuccess={handleImportSuccess}
      />
    </Container>
  );
};

export default connect()(OpenPGP);
